import morph from "../helpers/morph";
import { initializeTooltips } from "../global/tooltip/tooltip";

// Hover dwell so a sweep across a swatch grid costs one request, not one per swatch.
const PREFETCH_DWELL_MS = 120;

// Section renders take seconds; stale speculative requests hold connections the real click needs.
const MAX_CONCURRENT_PREFETCH = 2;

// A prefetched section carries stock and price, so it's only trusted this long.
const PREFETCH_TTL_MS = 30000;

// Max time a swap holds the old gallery while the new first slide loads.
const IMAGE_HOLD_MS = 500;

class ProductAjaxNavigation {
  constructor() {
    this.sectionSelector = "[data-main-product-section]";
    this.selectorContainerSelector = "[data-product-form-selectors]";
    this.swatchSelector = "[data-product-swatch-link]";

    // cacheKey -> { request, expires }; promises, so hover -> click shares one request.
    this.sectionCache = new Map();

    this.activePrefetches =
      new Map();

    this.prefetchTimer = null;

    this.pendingPrefetchPath =
      null;

    // Latest click wins; earlier in-flight clicks don't block later ones.
    this.requestSequence = 0;

    this.defaultChildInitialized = false;

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);

    this.handlePopState =
      this.handlePopState.bind(this);

    this.handlePrefetchIntent =
      this.handlePrefetchIntent.bind(this);

    this.init();
  }

  init() {
    if (!document.querySelector(this.sectionSelector)) {
      return;
    }

    if (window.productAjaxNavigationInitialized) {
      return;
    }

    window.productAjaxNavigationInitialized = true;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    document.addEventListener(
      "click",
      this.handleDocumentClick,
    );

    window.addEventListener(
      "popstate",
      this.handlePopState,
    );

    document.addEventListener(
      "pointerover",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    /*
     * Keyboard users.
     */
    document.addEventListener(
      "focusin",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    // Mobile: start on touchstart rather than waiting for click.
    document.addEventListener(
      "touchstart",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    this.initializeDefaultChild();
  }

  initializeDefaultChild() {
    if (this.defaultChildInitialized) {
      return;
    }

    const selectorContainer =
      document.querySelector(
        this.selectorContainerSelector,
      );

    const defaultChildUrl =
      selectorContainer?.dataset.defaultChildUrl;

    if (!defaultChildUrl) {
      return;
    }

    const currentUrl =
      new URL(window.location.href);

    const destinationUrl =
      new URL(
        defaultChildUrl,
        window.location.origin,
      );

    const isAlreadyDefaultChild =
      this.normalizePath(
        currentUrl.pathname,
      ) ===
      this.normalizePath(
        destinationUrl.pathname,
      );

    if (isAlreadyDefaultChild) {
      return;
    }

    this.defaultChildInitialized = true;

    this.loadProduct(
      destinationUrl,
      {
        historyAction: "replace",
      },
    );
  }

  handlePrefetchIntent(event) {
    if (navigator.connection?.saveData) {
      return;
    }

    const link =
      event.target.closest?.(
        this.swatchSelector,
      );

    if (!link?.href) {
      return;
    }

    const destinationUrl =
      new URL(
        link.href,
        window.location.origin,
      );

    const currentUrl =
      new URL(window.location.href);

    if (
      this.urlsMatch(
        currentUrl,
        destinationUrl,
      )
    ) {
      return;
    }

    /*
     * Touch is a committed gesture, so it skips the dwell.
     */
    if (
      event.type === "touchstart"
    ) {
      this.prefetchProduct(
        destinationUrl,
      );

      return;
    }

    const destinationPath =
      destinationUrl.pathname;

    // pointerover repeats on one swatch; only a different swatch restarts the dwell.
    if (
      this.pendingPrefetchPath ===
      destinationPath
    ) {
      return;
    }

    window.clearTimeout(
      this.prefetchTimer,
    );

    this.pendingPrefetchPath =
      destinationPath;

    this.prefetchTimer =
      window.setTimeout(() => {
        this.pendingPrefetchPath =
          null;

        this.prefetchProduct(
          destinationUrl,
        );
      }, PREFETCH_DWELL_MS);
  }

  handleDocumentClick(event) {
    const link =
      event.target.closest(
        this.swatchSelector,
      );

    if (!link) {
      return;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const currentUrl =
      new URL(window.location.href);

    const destinationUrl =
      new URL(
        link.href,
        window.location.origin,
      );

    if (
      this.urlsMatch(
        currentUrl,
        destinationUrl,
      )
    ) {
      event.preventDefault();

      return;
    }

    event.preventDefault();

    window.clearTimeout(
      this.prefetchTimer,
    );

    this.pendingPrefetchPath = null;

    this.selectSwatch(link);

    /*
     * Navigation begins immediately.
     */
    this.loadProduct(
      destinationUrl,
      {
        historyAction: "push",

        scrollToFormTop:
          link.hasAttribute(
            "data-scroll-to-form-top",
          ),
      },
    );
  }

  // Selects the clicked colour swatch straight away; the incoming section confirms it.
  selectSwatch(link) {
    if (
      !link.classList.contains(
        "product-swatch",
      )
    ) {
      return;
    }

    const scope =
      link.closest(
        this.selectorContainerSelector,
      ) ||
      link.closest(
        this.sectionSelector,
      );

    if (!scope) {
      return;
    }

    scope
      .querySelectorAll(
        ".product-swatch--selected, .product-swatch[aria-current]",
      )
      .forEach((swatch) => {
        swatch.classList.remove(
          "product-swatch--selected",
        );

        swatch.removeAttribute(
          "aria-current",
        );
      });

    link.classList.add(
      "product-swatch--selected",
    );

    link.setAttribute(
      "aria-current",
      "page",
    );
  }

  handlePopState() {
    this.loadProduct(
      new URL(window.location.href),
      {
        historyAction: "none",
      },
    );
  }

  normalizePath(pathname) {
    return (
      pathname.replace(/\/+$/, "") ||
      "/"
    );
  }

  urlsMatch(a, b) {
    return (
      this.normalizePath(a.pathname) ===
        this.normalizePath(b.pathname) &&
      a.search === b.search
    );
  }

  getSectionId() {
    return (
      document.querySelector(
        this.sectionSelector,
      )?.dataset.sectionId || null
    );
  }

  getCacheKey(
    destinationUrl,
    sectionId,
  ) {
    const url =
      new URL(destinationUrl.href);

    url.hash = "";

    url.searchParams.delete(
      "section_id",
    );

    return (
      `${sectionId}:` +
      `${url.pathname}` +
      `${url.search}`
    );
  }

  getSectionRequestUrl(
    destinationUrl,
    sectionId,
  ) {
    const url =
      new URL(destinationUrl.href);

    url.searchParams.set(
      "section_id",
      sectionId,
    );

    return url;
  }

  // The cached request for a key, or null once it has expired.
  getCachedRequest(cacheKey) {
    const entry =
      this.sectionCache.get(
        cacheKey,
      );

    if (!entry) {
      return null;
    }

    if (
      entry.expires < Date.now()
    ) {
      this.sectionCache.delete(
        cacheKey,
      );

      return null;
    }

    return entry.request;
  }

  async fetchSectionHtml(
    destinationUrl,
    sectionId,
    signal = null,
  ) {
    const cacheKey =
      this.getCacheKey(
        destinationUrl,
        sectionId,
      );

    // May already be an in-flight hover prefetch or a cached product.
    const cachedRequest =
      this.getCachedRequest(
        cacheKey,
      );

    // A navigation leaves the speculative pool (so a hover can't abort it) and
    // the cache (so the next visit to this colour gets fresh stock).
    if (!signal) {
      this.activePrefetches.delete(
        cacheKey,
      );

      this.sectionCache.delete(
        cacheKey,
      );
    }

    if (cachedRequest) {
      return cachedRequest;
    }

    const requestUrl =
      this.getSectionRequestUrl(
        destinationUrl,
        sectionId,
      );

    const request = fetch(
      requestUrl.href,
      {
        method: "GET",

        signal,

        headers: {
          Accept: "text/html",

          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load product section. Status: ${response.status}`,
          );
        }

        const html =
          await response.text();

        // Not awaited: start image loading while the user may still only be hovering.
        this.warmCriticalMedia(html);

        return html;
      })
      .catch((error) => {
        // Failed requests shouldn't poison the cache; only drop this request's own entry.
        if (
          this.sectionCache.get(
            cacheKey,
          )?.request === request
        ) {
          this.sectionCache.delete(
            cacheKey,
          );
        }

        throw error;
      });

    // Only prefetches are cached, so a click during one gets the same promise.
    if (signal) {
      this.sectionCache.set(
        cacheKey,
        {
          request,
          expires:
            Date.now() +
            PREFETCH_TTL_MS,
        },
      );
    }

    return request;
  }

  // Cache entry is dropped too, so a later click re-requests instead of awaiting a dead promise.
  evictOldestPrefetch() {
    while (
      this.activePrefetches.size >=
      MAX_CONCURRENT_PREFETCH
    ) {
      const [cacheKey, controller] =
        this.activePrefetches
          .entries()
          .next().value;

      this.activePrefetches.delete(
        cacheKey,
      );

      this.sectionCache.delete(
        cacheKey,
      );

      controller.abort();
    }
  }

  prefetchProduct(
    destinationUrl,
  ) {
    const sectionId =
      this.getSectionId();

    if (!sectionId) {
      return;
    }

    const cacheKey =
      this.getCacheKey(
        destinationUrl,
        sectionId,
      );

    if (
      this.getCachedRequest(
        cacheKey,
      )
    ) {
      return;
    }

    this.evictOldestPrefetch();

    const controller =
      new AbortController();

    this.activePrefetches.set(
      cacheKey,
      controller,
    );

    this.fetchSectionHtml(
      destinationUrl,
      sectionId,
      controller.signal,
    )
      .catch(() => {
        // Prefetch failure doesn't affect normal navigation.
      })
      .finally(() => {
        this.activePrefetches.delete(
          cacheKey,
        );
      });
  }

  isRenderableProductSection(section) {
    const productForm =
      section.querySelector(
        "[data-page-product-id]",
      );

    if (
      productForm &&
      !productForm.dataset.pageProductId
    ) {
      return false;
    }

    return !section.textContent.includes(
      "Liquid error",
    );
  }

  parseSection(html) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        "text/html",
      );

    return parsedDocument.querySelector(
      this.sectionSelector,
    );
  }

  // Two images: the desktop gallery shows part of slide 2, which would otherwise pop in.
  getCriticalImages(section) {
    const gallery =
      section?.querySelector(
        "media-gallery",
      );

    if (!gallery) {
      return [];
    }

    const wrappers =
      Array.from(
        gallery.querySelectorAll(
          ".media-gallery__media-wrapper",
        ),
      );

    if (!wrappers.length) {
      return [];
    }

    const requestedIndex =
      Number(
        gallery.dataset
          .initialMediaIndex,
      );

    const initialIndex =
      Number.isInteger(
        requestedIndex,
      )
        ? Math.max(
            0,
            Math.min(
              requestedIndex,
              wrappers.length - 1,
            ),
          )
        : 0;

    const indexes = [
      initialIndex,
      initialIndex + 1,
    ].filter(
      (index) =>
        index < wrappers.length,
    );

    return indexes
      .map((index) =>
        wrappers[index]?.querySelector(
          "img",
        ),
      )
      .filter(Boolean);
  }

  // Thumbnails are tiny (90/180w), so no two-image window needed here.
  getCriticalThumbnails(section) {
    // The rail is display: none below tablet-wide, so don't warm it there.
    const currentThumbnail =
      document.querySelector(
        "[data-gallery-original-thumbnail]",
      );

    if (
      !currentThumbnail?.getClientRects()
        .length
    ) {
      return [];
    }

    const gallery =
      section?.querySelector(
        "media-gallery",
      );

    if (!gallery) {
      return [];
    }

    return Array.from(
      gallery.querySelectorAll(
        "[data-gallery-original-thumbnail] img",
      ),
    );
  }

  resolveImageCandidate(image) {
    const picture =
      image.closest("picture");

    const source =
      picture &&
      Array.from(
        picture.querySelectorAll(
          "source",
        ),
      ).find((candidate) => {
        const media =
          candidate.getAttribute(
            "media",
          );

        return (
          !media ||
          window.matchMedia(media)
            .matches
        );
      });

    return {
      srcset:
        source?.getAttribute(
          "srcset",
        ) ||
        image.getAttribute("srcset"),

      sizes:
        source?.getAttribute(
          "sizes",
        ) ||
        image.getAttribute("sizes"),

      src: source
        ? null
        : image.getAttribute("src"),
    };
  }

  preloadImageElement(image) {
    return new Promise(
      (resolve) => {
        const preload =
          new Image();

        const {
          src,
          srcset,
          sizes,
        } =
          this.resolveImageCandidate(
            image,
          );

        if (sizes) {
          preload.sizes =
            sizes;
        }

        if (srcset) {
          preload.srcset =
            srcset;
        }

        if (src) {
          preload.src =
            src;
        }

        const done =
          async () => {
            try {
              await preload.decode?.();
            } catch (_) {
              // decode() failure must never block navigation.
            }

            resolve();
          };

        if (preload.complete) {
          done();

          return;
        }

        preload.addEventListener(
          "load",
          done,
          {
            once: true,
          },
        );

        preload.addEventListener(
          "error",
          resolve,
          {
            once: true,
          },
        );
      },
    );
  }

  warmCriticalMedia(html) {
    try {
      const section =
        this.parseSection(html);

      if (!section) {
        return;
      }

      // No timeout during prefetch; just start loading.
      const images =
        this.getCriticalImages(
          section,
        );

      const slidesWarmed =
        Promise.all(
          images.map((image) =>
            this.preloadImageElement(
              image,
            ),
          ),
        );

      // Thumbnails wait for slides so they never compete with the first image; preloadImageElement resolves on error too.
      slidesWarmed.then(() => {
        this.getCriticalThumbnails(
          section,
        ).forEach((thumbnail) => {
          this.preloadImageElement(
            thumbnail,
          );
        });
      });
    } catch (_) {
      /*
       * Performance enhancement only.
       */
    }
  }

  // Resolves once the new first slide is decoded, or after IMAGE_HOLD_MS, so the swap never shows a blank gallery.
  waitForCriticalImage(section) {
    const [image] =
      this.getCriticalImages(
        section,
      );

    // A video slide's <img> is fallback content that never shows.
    if (
      !image ||
      image.closest("video")
    ) {
      return Promise.resolve();
    }

    return Promise.race([
      this.preloadImageElement(
        image,
      ),
      new Promise((resolve) =>
        window.setTimeout(
          resolve,
          IMAGE_HOLD_MS,
        ),
      ),
    ]);
  }

  updateBrowserHistory(
    destinationUrl,
    historyAction,
  ) {
    if (
      historyAction === "none"
    ) {
      return;
    }

    const cleanUrl =
      new URL(destinationUrl.href);

    cleanUrl.searchParams.delete(
      "section_id",
    );

    const state = {
      productUrl:
        cleanUrl.href,
    };

    const browserUrl =
      `${cleanUrl.pathname}` +
      `${cleanUrl.search}` +
      `${cleanUrl.hash}`;

    if (
      historyAction === "replace"
    ) {
      window.history.replaceState(
        state,
        "",
        browserUrl,
      );

      return;
    }

    window.history.pushState(
      state,
      "",
      browserUrl,
    );
  }

  updateDocumentTitle(
    section,
  ) {
    const documentTitle =
      section.dataset
        .documentTitle;

    if (documentTitle) {
      document.title =
        documentTitle;
    }
  }

  dispatchRenderedEvent(
    section,
    destinationUrl,
  ) {
    document.dispatchEvent(
      new CustomEvent(
        "product:ajax-rendered",
        {
          detail: {
            url:
              destinationUrl.href,

            section,
          },
        },
      ),
    );
  }

  restoreScrollPosition(
    scrollY,
  ) {
    window.scrollTo({
      top: scrollY,
      left: 0,
      behavior: "auto",
    });
  }

  scrollToFormTop(section) {
    const target =
      section.querySelector(
        ".main-product__details-wrapper",
      ) || section;

    const rootStyle =
      getComputedStyle(
        document.documentElement,
      );

    const headerHeight =
      parseFloat(
        rootStyle.getPropertyValue(
          "--header-height",
        ),
      ) || 0;

    const promoBarHeight =
      parseFloat(
        rootStyle.getPropertyValue(
          "--promo-bar-height",
        ),
      ) || 0;

    const targetTop =
      target.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      promoBarHeight -
      16;

    window.scrollTo({
      top:
        Math.max(
          targetTop,
          0,
        ),

      left: 0,

      behavior: "smooth",
    });
  }

  async loadProduct(
    destinationUrl,
    {
      historyAction = "push",
      scrollToFormTop = false,
    } = {},
  ) {
    const currentSection =
      document.querySelector(
        this.sectionSelector,
      );

    if (!currentSection) {
      return;
    }

    const sectionId =
      currentSection.dataset
        .sectionId;

    if (!sectionId) {
      window.location.href =
        destinationUrl.href;

      return;
    }

    // Latest navigation owns the page; don't disable swatches while loading.
    const requestId =
      ++this.requestSequence;

    const savedScrollY =
      window.scrollY;

    /*
     * Address bar changes immediately.
     */
    this.updateBrowserHistory(
      destinationUrl,
      historyAction,
    );

    currentSection.setAttribute(
      "aria-busy",
      "true",
    );

    try {
      const html =
        await this.fetchSectionHtml(
          destinationUrl,
          sectionId,
        );

      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      const newSection =
        this.parseSection(html);

      if (!newSection) {
        throw new Error(
          "Updated product section was not found.",
        );
      }

      // Shopify can return 200 with no `product` (redirect / not renderable -> Liquid error);
      // never morph that in, the full-page fallback below handles it.
      if (!this.isRenderableProductSection(newSection)) {
        throw new Error(
          "Updated product section was rendered without a product.",
        );
      }

      await this.waitForCriticalImage(
        newSection,
      );

      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      morph(
        currentSection,
        newSection,
      );

      const updatedSection =
        document.querySelector(
          this.sectionSelector,
        );

      if (!updatedSection) {
        throw new Error(
          "Updated product section was not found after morph.",
        );
      }

      initializeTooltips(
        updatedSection,
      );

      this.updateDocumentTitle(
        updatedSection,
      );

      updatedSection.removeAttribute(
        "aria-busy",
      );

      if (scrollToFormTop) {
        this.scrollToFormTop(
          updatedSection,
        );
      } else {
        this.restoreScrollPosition(
          savedScrollY,
        );
      }

      this.dispatchRenderedEvent(
        updatedSection,
        destinationUrl,
      );

      // Don't block on sections outside the primary PDP.
      this.refreshReactiveSections(
        destinationUrl,
      );
    } catch (error) {
      /*
       * Ignore errors from an older/stale request.
       */
      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      console.error(
        "Product AJAX navigation failed:",
        error,
      );

      /*
       * Safety fallback.
       */
      window.location.href =
        destinationUrl.href;
    } finally {
      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      document
        .querySelector(
          this.sectionSelector,
        )
        ?.removeAttribute(
          "aria-busy",
        );
    }
  }

  getReactiveSections() {
    return Array.from(
      document.querySelectorAll(
        "[data-ajax-reactive-section]",
      ),
    ).filter(
      (element) =>
        !element.closest(
          this.sectionSelector,
        ),
    );
  }

  async refreshReactiveSections(
    destinationUrl,
  ) {
    const reactiveSections =
      this.getReactiveSections();

    if (
      !reactiveSections.length
    ) {
      return;
    }

    await Promise.all(
      reactiveSections.map(
        (section) =>
          this.refreshReactiveSection(
            section,
            destinationUrl,
          ),
      ),
    );
  }

  async refreshReactiveSection(
    currentEl,
    destinationUrl,
  ) {
    const sectionId =
      currentEl.dataset
        .sectionId;

    if (!sectionId) {
      return;
    }

    try {
      const requestUrl =
        new URL(
          destinationUrl.href,
        );

      requestUrl.searchParams.set(
        "section_id",
        sectionId,
      );

      const response =
        await fetch(
          requestUrl.href,
          {
            method: "GET",

            headers: {
              Accept:
                "text/html",

              "X-Requested-With":
                "XMLHttpRequest",
            },
          },
        );

      if (!response.ok) {
        return;
      }

      const html =
        await response.text();

      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          "text/html",
        );

      const newEl =
        parsedDocument.querySelector(
          `[data-ajax-reactive-section][data-section-id="${sectionId}"]`,
        );

      if (!newEl) {
        currentEl.remove();

        return;
      }

      morph(
        currentEl,
        newEl,
      );
    } catch (error) {
      console.error(
        `Failed to refresh reactive section "${sectionId}":`,
        error,
      );
    }
  }
}

new ProductAjaxNavigation();